import React from "react";
import {
  MdArrowOutward,
  MdCalendarMonth,
  MdDomain,
  MdGroups2,
  MdLocationOn,
  MdOutlineAccountBalance,
  MdOutlineInsights,
  MdOutlinePublic,
  MdVerified,
} from "react-icons/md";

import EventNavigation from "../EventNavigation/EventNavigation";
import Footer from "../Footer/Footer";
import "./arce_page.css";

const facts = [
  { value: "03", label: "days in Accra" },
  { value: "03", label: "core pillars" },
  { value: "01", label: "African ecosystem" },
  { value: "2026", label: "the year to connect" },
];

const pillars = [
  {
    icon: MdOutlineAccountBalance,
    title: "Policy & Regulation",
    text: "Create the clear frameworks, trusted standards, and collaborative governance needed for responsible real estate growth.",
  },
  {
    icon: MdOutlineInsights,
    title: "PropTech Innovation",
    text: "Explore the smart technology, data, and practical ideas reshaping how property is discovered, developed, and transacted.",
  },
  {
    icon: MdOutlinePublic,
    title: "Sustainable Development",
    text: "Champion development that supports people, prosperity, and the planet across Africa's future cities.",
  },
];

const audiences = [
  {
    title: "Policy leaders",
    text: "Meet ministers, regulators, and public-sector leaders shaping the rules and infrastructure that help markets thrive.",
  },
  {
    title: "Capital & developers",
    text: "Connect investors, banks, funds, developers, and institutions around Africa's next real estate opportunities.",
  },
  {
    title: "Built-environment innovators",
    text: "Bring together PropTech companies, planners, architects, construction firms, academics, and sustainability specialists.",
  },
];

const outcomes = [
  { code: "TRUST", title: "Build market confidence", text: "Advance transparency, stronger standards, and durable confidence across the sector." },
  { code: "ALIGN", title: "Connect policy and industry", text: "Bring institutions and practitioners into the same room to create responsible growth." },
  { code: "GROW", title: "Unlock innovation and investment", text: "Put practical technology and investable ideas at the centre of the conversation." },
];

const ArcePage = () => (
  <>
    <EventNavigation />

    <main className="arce-page">
      <section className="arce-hero">
        <div className="arce-hero-grid" />
        <div className="arce-sun arce-sun-one" />
        <div className="arce-sun arce-sun-two" />
        <div className="arce-shell arce-hero-content">
          <div className="arce-hero-copy">
            <p className="arce-kicker"><span /> Africa's premier real estate conference</p>
            <h1><strong>ARCE</strong><em>Africa Real Estate Conference & Expo</em></h1>
            <p className="arce-hero-lede">Uniting Africa's real estate ecosystem to advance trust, technology, and sustainable growth.</p>
            <div className="arce-event-details" aria-label="Event details">
              <span><MdCalendarMonth aria-hidden="true" /> October 14–16, 2026</span>
              <span><MdLocationOn aria-hidden="true" /> The Palms Convention Center, Accra, Ghana</span>
            </div>
            <div className="arce-hero-actions">
              <a className="arce-button arce-button-primary" href="https://arceconference.com/" target="_blank" rel="noopener noreferrer">
                Register for ARCE <MdArrowOutward aria-hidden="true" />
              </a>
              <a className="arce-button arce-button-quiet" href="#why-arce">Why ARCE</a>
            </div>
          </div>
          <aside className="arce-hero-card" aria-label="ARCE event summary">
            <div className="arce-hero-card-mark">26</div>
            <p>Accra, Ghana</p>
            <h2>Where Africa's property future takes shape.</h2>
            <div className="arce-hero-card-rule" />
            <span><MdVerified aria-hidden="true" /> A continent-wide platform for purposeful conversations and practical progress.</span>
          </aside>
        </div>
      </section>

      <section className="arce-facts" aria-label="ARCE at a glance">
        <div className="arce-shell arce-facts-grid">
          {facts.map((fact) => <div className="arce-fact" key={fact.label}><strong>{fact.value}</strong><span>{fact.label}</span></div>)}
        </div>
      </section>

      <section className="arce-section arce-introduction">
        <div className="arce-shell arce-introduction-grid">
          <div className="arce-section-heading"><p className="arce-kicker arce-kicker-dark">About ARCE 2026</p><h2>Real estate's next chapter is an African conversation.</h2></div>
          <div className="arce-prose">
            <p>ARCE 2026 brings together real estate leaders, policymakers, investors, developers, institutions, and PropTech innovators from across Africa.</p>
            <p>It is a focused place to build trust, share knowledge, and develop the partnerships that make sustainable, technology-enabled growth possible.</p>
          </div>
        </div>
      </section>

      <section className="arce-section arce-pillars-section">
        <div className="arce-shell">
          <div className="arce-section-intro"><p className="arce-kicker arce-kicker-dark">The foundation</p><h2>Three pillars for a more resilient property future.</h2></div>
          <div className="arce-pillars-grid">
            {pillars.map(({ icon: Icon, title, text }, index) => <article className="arce-pillar" key={title}><span className="arce-pillar-number">0{index + 1}</span><div className="arce-pillar-icon"><Icon aria-hidden="true" /></div><h3>{title}</h3><p>{text}</p></article>)}
          </div>
        </div>
      </section>

      <section className="arce-section arce-why" id="why-arce">
        <div className="arce-shell">
          <div className="arce-why-banner"><div><p className="arce-kicker">Why ARCE matters</p><h2>Build the future of real estate in Africa.</h2></div><p>ARCE is a platform for the people and ideas moving Africa's real estate sector forward—through policy, innovation, investment, and better urban development.</p></div>
          <div className="arce-audience-grid">
            {audiences.map((audience) => <article className="arce-audience-card" key={audience.title}><MdGroups2 aria-hidden="true" /><h3>{audience.title}</h3><p>{audience.text}</p></article>)}
          </div>
        </div>
      </section>

      <section className="arce-section arce-difference">
        <div className="arce-shell arce-difference-layout">
          <div className="arce-difference-copy"><p className="arce-kicker arce-kicker-dark">A shared agenda</p><h2>More than a conference floor.</h2><p>ARCE connects the stakeholders that influence markets today with the innovators and institutions building their long-term potential.</p><a className="arce-button arce-difference-button" href="https://arceconference.com/" target="_blank" rel="noopener noreferrer">Explore ARCE 2026 <MdArrowOutward aria-hidden="true" /></a></div>
          <div className="arce-outcomes-stack">
            {outcomes.map((outcome) => <article className="arce-outcome" key={outcome.code}><span>{outcome.code}</span><div><h3>{outcome.title}</h3><p>{outcome.text}</p></div></article>)}
          </div>
        </div>
      </section>

      <section className="arce-section arce-location"><div className="arce-shell arce-location-card"><div className="arce-location-icon"><MdDomain aria-hidden="true" /></div><div><p className="arce-kicker arce-kicker-dark">Accra, Ghana</p><h2>A meeting point for continental momentum.</h2></div><p>At The Palms Convention Center, ARCE 2026 creates room for the region's decision-makers to share direction, make connections, and put meaningful ideas into action.</p></div></section>

      <section className="arce-closing"><div className="arce-shell arce-closing-inner"><p className="arce-kicker">October 14–16, 2026</p><h2>Be part of Africa's next real estate conversation.</h2><p>Join ARCE 2026 in Accra and help shape a more trusted, innovative, and sustainable built environment.</p><a className="arce-button arce-button-primary" href="https://arceconference.com/" target="_blank" rel="noopener noreferrer">Visit arceconference.com <MdArrowOutward aria-hidden="true" /></a></div></section>
    </main>

    <Footer />
  </>
);

export default ArcePage;
